import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsV4PayoutsPayoutsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/wixpay-payout',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/wixpay-payout/v4/payout-groups',
        destPath: '/v4/payout-groups',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/payouts',
        destPath: '/v4/payouts',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/wixpay-payout/v4/payouts',
        destPath: '/v4/payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/payout-groups',
        destPath: '/v4/payout-groups',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payouts',
        destPath: '/v4/bo-payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payout-policies',
        destPath: '/v4/bo-payout-policies',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/wixpay-payout/v4/payouts',
        destPath: '/v4/payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/payout-groups',
        destPath: '/v4/payout-groups',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payouts',
        destPath: '/v4/bo-payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payout-policies',
        destPath: '/v4/bo-payout-policies',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/wixpay-payout/v4/payouts',
        destPath: '/v4/payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/payout-groups',
        destPath: '/v4/payout-groups',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payouts',
        destPath: '/v4/bo-payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/bo-payout-policies',
        destPath: '/v4/bo-payout-policies',
      },
    ],
    'payment-webhooks.wixapps.net': [
      {
        srcPath: '/wix-payments/payout-callbacks/stripe',
        destPath: '/payout-callbacks/stripe',
      },
      {
        srcPath: '/wix-payments/payout-callbacks/adyen',
        destPath: '/payout-callbacks/adyen',
      },
      {
        srcPath: '/wix-payments/payout-callbacks/stone',
        destPath: '/payout-callbacks/stone',
      },
      {
        srcPath: '/wix-payments/payout-callbacks/paypal',
        destPath: '/payout-callbacks/paypal',
      },
    ],
    'cronulla-jobs._base_domain_': [
      {
        srcPath: '/wix-payments/wixpay-payout/internal-api/jobs',
        destPath: '/internal-api/jobs',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/wixpay-payouts/v4/payouts',
        destPath: '/v4/payouts',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/payments/payouts/v4/payouts',
        destPath: '/v4/payouts',
      },
    ],
    _: [
      {
        srcPath: '/payments/payouts/v4/payouts',
        destPath: '/v4/payouts',
      },
    ],
    'payments.base44.com': [
      {
        srcPath: '/_api/wixpay-payout/v4/payouts',
        destPath: '/v4/payouts',
      },
      {
        srcPath: '/_api/wixpay-payout/v4/payout-groups',
        destPath: '/v4/payout-groups',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_payouts';

/**
 * Retrieves a paginated list of payouts for the specified Wix Payments account.
 *
 * Supports optional filters by account profile and by creation date range.
 * A newly created payout can take a short time to appear in the results.
 *
 * Calls are limited to 100 requests per 10 seconds per caller. Requests
 * over the limit fail with a `RESOURCE_EXHAUSTED` error and the message
 * `The request was throttled`.
 */
export function listPayouts(payload: object): RequestOptionsFactory<any> {
  function __listPayouts({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'createdBefore' }, { path: 'createdAfter' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'sort.origin.latitude' },
          { path: 'sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.payments.payouts.v4.payout',
      method: 'GET' as any,
      methodFqn: 'wix.payments.v4.payouts.PayoutsService.ListPayouts',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsV4PayoutsPayoutsServiceUrl({
        protoPath: '/v4/payouts',
        data: serializedData,
        host,
      }),
      params: toURLSearchParams(serializedData, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'payouts.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __listPayouts;
}

/**
 * Retrieves a single payout by ID.
 *
 * Calls are limited to 100 requests per 10 seconds per caller. Requests
 * over the limit fail with a `RESOURCE_EXHAUSTED` error and the message
 * `The request was throttled`.
 */
export function getPayout(payload: object): RequestOptionsFactory<any> {
  function __getPayout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.payments.payouts.v4.payout',
      method: 'GET' as any,
      methodFqn: 'wix.payments.v4.payouts.PayoutsService.GetPayout',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsV4PayoutsPayoutsServiceUrl({
        protoPath: '/v4/payouts/{payoutId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'payout.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __getPayout;
}
